import { Tabs } from 'antd';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect, useDispatch } from 'react-redux';

import { getOpenedTaskId } from '../../../../redux/general/selectors.js';
import {
  registerComponentLevelSocketListener,
  unregisterComponentLevelSocketListener,
} from '../../../../redux/state-areas/socket/actions.js';

import EditableTaskDetailTitle from './components/EditableTaskDetailTitle.jsx';
import ParentTaskBreadCrumbs from './components/ParentTaskBreadCrumbs.jsx';
import TaskDetailsCompletedLabel from './components/TaskDetailsCompletedLabel.jsx';
import TaskDetailsFileDropArea from './components/TaskDetailsFileDropArea.jsx';
import TaskDetailsCreatedBy from './components/TaskDetailsCreatedBy.jsx';
import TaskDetailsAssignedTo from './components/TaskDetailsAssignedTo.jsx';
import TaskDetailsPriority from './components/TaskDetailsPriority.jsx';
import TaskDetailsDueDate from './components/TaskDetailsDueDate.jsx';
import TaskDetailsRecurringTasks from './components/TaskDetailsRecurringTasks.jsx';
import TaskDetailsReminder from './components/TaskDetailsReminder.jsx';
import TaskDetailsProjects from './components/TaskDetailsProjects.jsx';
import TaskDetailsCustomFields from './components/TaskDetailsCustomFields.jsx';
import TaskDetailsPayment from './components/TaskDetailsPayment.jsx';
import TaskDetailsDescription from './components/TaskDetailsDescription.jsx';
import TaskDetailsArchivedLabel from './components/TaskDetailsArchivedLabel.jsx';
import TaskDetailsTags from './components/TaskDetailsTags.jsx';
import TaskDetailsSubtasks from './components/TaskDetailsSubtasks.jsx';
import TaskDetailsFileHolder from './components/TaskDetailsFileHolder.jsx';
import TaskDetailsFileInput from './components/TaskDetailsFileInput.jsx';
import TaskDetailsCollaborators from './components/TaskDetailsCollaborators.jsx';
import TaskDetailsCommentsInput from './components/TaskDetailsCommentsInput.jsx';
import TaskDetailsActivitySection from './components/TaskDetailsActivitySection.jsx';
import TaskDetailsCommentsTabLabel from './components/TaskDetailsCommentsTabLabel.jsx';
import TaskDetailsCommentSection from './components/TaskDetailsCommentSection';
import TaskDetailsFilesSection from './components/TaskDetailsFilesSection.jsx';
import TaskDetailsFilesTabLabel from './components/TaskDetailsFilesTabLabel.jsx';
import TaskParentTaskSearch from './TaskParentTaskSearch.jsx';

const tabBarStyle = { padding: '0 15px' };

const TaskDetailSection = ({ isModal = false, projectId, selectedTab, setSelectedTab, taskId, showParentTaskSearch = false, setShowParentTaskSearch, subTask }) => {
  const dispatch = useDispatch();
  const taskScrollRef = useRef();

  const [selectedTabIndex, setSelectedTabIndex] = useState('1');

  const taskDetailsBodyStyle = useMemo(
    () => ({
      overflowY: 'scroll',
      height: '100%',
      background: 'white',
      width: isModal && '50%',
      scrollBehavior: 'smooth',
      paddingRight: isModal ? 20 : 0,
      paddingBottom: isModal ? 40 : 0,
    }),
    [isModal],
  );

  const tabsContent = useMemo(
    () => [
      {
        key: '1',
        label: <TaskDetailsCommentsTabLabel />,
        className: 'task-details-comments-tab-panel',
        children: <TaskDetailsCommentSection isModal={isModal} />,
      },
      {
        key: '2',
        label: <FormattedMessage id="ACTIVITY" defaultMessage="Activity" />,
        className: 'task-details-activity-tab-panel',
        children: <TaskDetailsActivitySection />,
      },
      {
        key: '4',
        label: <TaskDetailsFilesTabLabel />,
        children: <TaskDetailsFilesSection />,
      },
    ],
    [isModal],
  );

  const tabsSectionRenderer = useCallback(() => {
    return (
      <div className={isModal ? 'task-modal-reply-section' : 'task-reply-section'}>
        <Tabs
          activeKey={selectedTabIndex}
          items={tabsContent}
          onChange={(index) => {
            setSelectedTab?.(index);
            setSelectedTabIndex(index);
          }}
          tabBarStyle={tabBarStyle}
          className="m-0 h-full"
        />
      </div>
    );
  }, [isModal, selectedTabIndex, setSelectedTab, tabsContent]);

  useEffect(() => {
    console.info(`[TaskDetailSection]: Opened task ${taskId} in a ${isModal ? 'modal': 'side'} view`);
  }, [isModal, taskId]);

  useEffect(() => {
    if (!taskScrollRef || !taskScrollRef.current) return;

    taskScrollRef.current.scrollTop = 0;
  }, [taskId]);

  useEffect(() => {
    if (!taskScrollRef || !taskScrollRef.current || isModal) return;

    const listenerId = dispatch(
      registerComponentLevelSocketListener('message-received', (msg) => {
        if (msg?.taskId?.toString() === taskId?.toString()) {
          const scrollHeight = taskScrollRef?.current?.scrollHeight;
          const height = taskScrollRef?.current?.clientHeight;
          const maxScrollTop = scrollHeight - height;
          setTimeout(() => {
            if (taskScrollRef && taskScrollRef.current) {
              taskScrollRef.current.scrollTop = maxScrollTop > 0 ? maxScrollTop + 100 : 0;
            }
          }, 100);
        }
      }),
    );

    return () => {
      dispatch(unregisterComponentLevelSocketListener('message-received', listenerId));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModal, taskId]);

  useEffect(() => {
    setSelectedTabIndex(selectedTab);
  }, [selectedTab]);

  return (
    <div className={isModal ? 'task-detail-modal' : 'task-detail-section'}>
      <div ref={taskScrollRef} style={taskDetailsBodyStyle} className={isModal ? 'task-detail-body' : undefined}>
        <TaskDetailsFileDropArea isModal={isModal} />
        <TaskDetailsCompletedLabel />
        <ParentTaskBreadCrumbs />
        <TaskDetailsArchivedLabel projectId={projectId} />
        <div className="pb-2 pt-3 px-4 bg-white relative">
          {showParentTaskSearch ?
          <TaskParentTaskSearch subTask={subTask} setShowParentTaskSearch={setShowParentTaskSearch} /> : <EditableTaskDetailTitle />}
          <TaskDetailsCreatedBy />
          <div className="flex flex-col mt-1">
            <TaskDetailsAssignedTo />
            <TaskDetailsDueDate isModal={isModal} />
            <TaskDetailsRecurringTasks isModal={isModal} />
            <TaskDetailsReminder />
            <TaskDetailsPriority />
            <TaskDetailsProjects />
            <TaskDetailsCustomFields projectId={projectId} />
            <TaskDetailsPayment />
            <TaskDetailsDescription isModal={isModal} />
            <TaskDetailsTags />
            <TaskDetailsSubtasks />
            <TaskDetailsFileHolder isModal={isModal} />
          </div>
        </div>
        {!isModal && tabsSectionRenderer()}
      </div>
      <div className={`task-details-footer ${isModal ? 'modal-comment-section' : 'pt-2'}`}>
        {isModal && tabsSectionRenderer()}
        <div className="flex flex-col">
          {!isModal && selectedTabIndex === '1' && <TaskDetailsCommentsInput />}
          <TaskDetailsFileInput isDropAreaShown={selectedTabIndex === '4'} />
          <TaskDetailsCollaborators />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  taskId: getOpenedTaskId(state),
});

export default connect(mapStateToProps)(TaskDetailSection);